import { IconLock, IconSolarPanel } from '@tabler/icons-react';
import type {
  SidecarSidebarEntryMetadata,
  SidecarTileMetadata,
} from '../../tiles';

/**
 * Surface metadata for the Solarbox sidecar — the *display* contracts
 * the parent app needs to know about to mount the sidecar's UI in its
 * own shell. Two surfaces today:
 *
 *   - `solarboxTileMetadata` — the MainModules tile on the Rakine
 *     org-root page (`<AppMainModules>` grid).
 *   - `solarboxSidebarEntryMetadata` — the left-nav entry inside the
 *     Rakine app shell (`<SidebarNavItems>`).
 *
 * Both surfaces share the same icon and route — only the label/title
 * resolver differs because tiles use `title` and the sidebar uses
 * `label`. Keeping both descriptors in this single file (rather than
 * one file per surface) makes the sidecar's display contract a
 * single-file read: open `surfaces.tsx`, see every place the sidecar
 * shows up.
 *
 * `title` / `label` / `description` are language-message resolvers so
 * the sidecar owns its own i18n keys — `<AppSidecarTiles>` and
 * `<AppSidecarSidebarEntries>` each pass `messages` through from
 * `useLanguage()`.
 */
export const solarboxTileMetadata: SidecarTileMetadata = {
  id: 'solarbox-customers',
  title: messages =>
    messages.rakine?.modules?.solarboxCustomers || 'Paying customers',
  description: messages =>
    messages.rakine?.modules?.solarboxCustomersDesc ||
    'Solarbox subscribers and payment history',
  icon: <IconSolarPanel />,
  routePath: '/solarbox/customers',
};

/**
 * Sidebar-nav metadata, parallel to `solarboxTileMetadata`. Same
 * route — only the label resolver differs (tiles want a `title`, the
 * sidebar wants a `label`; for solarbox they're both "Clients
 * payants" but the contracts stay independent so future sidecars can
 * diverge).
 *
 * `value` is the `OrgRoute` literal used by `useRoute().activeLink`
 * for active-route highlighting; the matching branch lives in
 * `getActiveLink` (`client/src/hooks/useRoute.ts`).
 *
 * `iconName` keys into `SidebarNavItems`'s `ICON_MAP` for size-18
 * rendering (the sidebar wraps icons in a Tooltip when collapsed and
 * needs a consistent stroke/size). Using a string key (rather than
 * passing the JSX node directly) keeps the sidebar's icon rendering
 * uniform with the core entries.
 */
export const solarboxSidebarEntryMetadata: SidecarSidebarEntryMetadata = {
  value: 'solarbox-customers',
  label: messages =>
    messages.rakine?.modules?.solarboxCustomers || 'Paying customers',
  iconName: 'solar-panel',
  routePath: '/solarbox/customers',
};

/**
 * Bike-control surface metadata — peer to the customers entry, NOT
 * nested under a "Solarbox" group. Two top-level entries is the
 * v1 IA choice (see PR thread); collapse into a group later if a
 * third Solarbox surface lands.
 *
 * Same gating row (`org_app_sidecars` for `rakine`/`solarbox`)
 * controls both — sub-surface authorization isn't separated today.
 */
export const solarboxBikeControlTileMetadata: SidecarTileMetadata = {
  id: 'solarbox-bike-control',
  title: messages =>
    messages.rakine?.modules?.solarboxBikeControl || 'Bike control',
  description: messages =>
    messages.rakine?.modules?.solarboxBikeControlDesc ||
    'Lock or unlock a Solarbox bike by QR code',
  icon: <IconLock />,
  routePath: '/solarbox/bike-control',
};

export const solarboxBikeControlSidebarEntryMetadata: SidecarSidebarEntryMetadata =
  {
    value: 'solarbox-bike-control',
    label: messages =>
      messages.rakine?.modules?.solarboxBikeControl || 'Bike control',
    iconName: 'lock',
    routePath: '/solarbox/bike-control',
  };
